// ============================================
// 子构件定位与构件树解析工具
// 阶段8 补漏：绑定规则 ruleConfig.subName 定位 FBX 内部命名构件
// 用户要求：用 FBX 内部真实构件（罐体/厂房/管道/泵阀）做动画，不操作整体模型
// 仅定位（findSubObject）与收集 Mesh（collectMeshes），不修改 position/rotation/parent
// 2026-09-08 阶段1：parseSubObjects 全深度构件树解析（uuid/parentId/depth）
// ============================================

import * as THREE from 'three';
import type { SubObjectNode } from '@dtwin/shared';

/** 在 group 树中按 name 查找子对象（第一个匹配；subName 为空 → 返回 group 自身） */
export function findSubObject(
  group: THREE.Object3D | null,
  subName?: string,
): THREE.Object3D | null {
  if (!group) return null;
  if (!subName) return group;
  let found: THREE.Object3D | null = null;
  group.traverse((c) => {
    if (!found && c.name === subName) found = c;
  });
  return found;
}

/** 2026-09-08｜阶段4：按 uuid 查找子对象（组合模型内部节点稳定定位，getObjectById 内部实现） */
export function findSubObjectByUuid(
  group: THREE.Object3D | null,
  uuid?: string,
): THREE.Object3D | null {
  if (!group || !uuid) return null;
  return group.getObjectByProperty('uuid', uuid) ?? null;
}

/** 2026-09-08｜阶段4：规则目标定位——优先 subObjectUuid（uuid 稳定），回落 subName（按名兼容旧数据） */
export function resolveRuleTarget(
  group: THREE.Object3D | null,
  ruleConfig?: Record<string, unknown> | null,
): THREE.Object3D | null {
  if (!group) return null;
  const uuid = (ruleConfig as any)?.subObjectUuid as string | undefined;
  const subName = (ruleConfig as any)?.subName as string | undefined;
  if (uuid) return findSubObjectByUuid(group, uuid);
  return findSubObject(group, subName);
}

/** 收集目标 Mesh：subName 匹配节点的 Mesh（含其子树）；subName 为空 → 整树 Mesh */
export function collectMeshes(
  group: THREE.Object3D | null,
  subName?: string,
): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  if (!group) return meshes;
  if (subName) {
    const target = findSubObject(group, subName);
    if (!target) return meshes;
    target.traverse((c) => {
      if ((c as any).isMesh) meshes.push(c as THREE.Mesh);
    });
  } else {
    group.traverse((c) => {
      if ((c as any).isMesh) meshes.push(c as THREE.Mesh);
    });
  }
  return meshes;
}

/**
 * 从 three 场景解析构件树（全深度递归：Group/Mesh/Bone 全部保留，uuid 稳定序列化标识）
 * 2026-09-08｜阶段1：由两层解析升级为全深度遍历，不修改原始 object3D 父子关系
 * 2026-09-08｜修复：生成 children 索引路径 path（uuid 跨会话随机变化，path 解析顺序确定）
 */
export function parseSubObjects(group: THREE.Object3D): SubObjectNode[] {
  const toNode = (obj: THREE.Object3D, parentId: string | null, depth: number, path: number[]): SubObjectNode => {
    const node: SubObjectNode = {
      name: obj.name || `${obj.type}_${obj.uuid.slice(0, 8)}`,
      type: obj.type,
      isMesh: (obj as any).isMesh === true,
      uuid: obj.uuid,
      parentId,
      depth,
      path,
      children: [],
    };
    obj.children.forEach((child, i) => {
      node.children.push(toNode(child, obj.uuid, depth + 1, [...path, i]));
    });
    return node;
  };
  return group.children.map((child, i) => toNode(child, group.uuid, 0, [i]));
}

/** 2026-09-08｜修复：按 children 索引路径在对象树中定位（uuid 失配跨会话恢复用） */
export function findByIndexPath(root: THREE.Object3D, path?: number[]): THREE.Object3D | null {
  if (!path?.length) return null;
  let cur: THREE.Object3D | null = root;
  for (const idx of path) {
    if (!cur?.children || !cur.children[idx]) return null;
    cur = cur.children[idx];
  }
  return cur;
}

/**
 * 2026-09-14｜阶段 C1：部件深拷贝（小部件复制/剪切用）
 * - 层级/几何深拷贝（Object3D.clone(true)）
 * - **材质深拷贝**（GLB/FBX 多 mesh 常共享同一材质实例——不 clone 会导致改副本连带原部件变色）
 * - 不修改父子关系（调用方负责挂到目标父节点；保持 local 变换）
 */
export function deepCloneObject(src: THREE.Object3D): THREE.Object3D {
  const copy = src.clone(true);
  copy.traverse((child: any) => {
    if (child.isMesh && child.material) {
      child.material = Array.isArray(child.material)
        ? child.material.map((m: any) => (m?.clone ? m.clone() : m))
        : (child.material?.clone ? child.material.clone() : child.material);
    }
  });
  return copy;
}

// ============================================
// 2026-09-14：同源部件判定（快速选中同类部件——对齐 Blender「选择关联→材质」/ Unity「Select by Material」）
// 说明：高亮/材质编辑会 clone 材质独立化（__dtwHighlightCloned/__dtwCloned），原共享关系被打散；
// 故 clone 时记录源材质 uuid（__dtwOriginMatUuid），同源判定用"源材质 id"。
// ============================================

/** 材质源 id（clone 后的材质回溯到原始共享材质 uuid；未 clone 的用自身 uuid） */
export function getMaterialSourceId(mat: any): string | null {
  if (!mat || typeof mat !== 'object') return null;
  return mat.__dtwOriginMatUuid || mat.uuid || null;
}

/**
 * 查与指定节点"同源"（使用同一源材质）的所有 Mesh uuid。
 * @param root 模型根对象
 * @param uuid 目标节点 uuid（可为 Mesh 或 Group——Group 时取其全部子 Mesh 的源材质集合）
 * @returns 同源 Mesh 的 uuid 列表（含目标自身涉及的 Mesh；去重）
 */
export function findSameMaterialMeshUuids(root: THREE.Object3D, uuid: string): string[] {
  const target = root.getObjectByProperty('uuid', uuid);
  if (!target) return [];
  // 1) 收集目标涉及的源材质 id 集合
  const sourceIds = new Set<string>();
  target.traverse((child: any) => {
    if (!child.isMesh || !child.material) return;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    mats.forEach((m: any) => {
      const sid = getMaterialSourceId(m);
      if (sid) sourceIds.add(sid);
    });
  });
  if (sourceIds.size === 0) return [];
  // 2) 全模型匹配同源 Mesh
  const out = new Set<string>();
  root.traverse((child: any) => {
    if (!child.isMesh || !child.material) return;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    if (mats.some((m: any) => {
      const sid = getMaterialSourceId(m);
      return sid !== null && sourceIds.has(sid);
    })) {
      out.add(child.uuid);
    }
  });
  return [...out];
}


/** 2026-09-14｜C4：在构件树中按 uuid 查找节点路径字符串（"1,0,0"；找不到返回 null） */
export function findNodePathStr(nodes: SubObjectNode[] | undefined, uuid: string): string | null {
  if (!nodes) return null;
  for (const n of nodes) {
    if (n.uuid === uuid) return (n.path ?? []).join(',');
    const child = findNodePathStr(n.children, uuid);
    if (child !== null) return child;
  }
  return null;
}
